import { pb } from "@/lib/pocketbase";

export interface GenerateArticleRequest {
  brandId: string;
  keyword?: string;
  topic?: string;
}

export interface GenerateArticleResponse {
  success: boolean;
  message: string;
  data: {
    postId: string;
  };
}

// ============================================================================
// LISTICLE TYPES AND FUNCTIONS
// ============================================================================

export interface ListicleItem {
  id: string;
  name: string;
  website: string;
  description?: string;
}

export interface GenerateListicleItemsRequest {
  topic: string;
  user_id: string;
}

export interface GenerateListicleItemsResponse {
  success: boolean;
  data?: {
    items: ListicleItem[];
    topic: string;
    total_items: number;
  };
  error?: string;
}

export interface GenerateListicleArticleRequest {
  topic: string;
  items: Array<{
    name: string;
    website: string;
  }>;
  user_id: string;
  brand_id: string;
}

export interface GenerateListicleArticleResponse {
  success: boolean;
  message: string;
  data?: {
    postId: string;
    topic: string;
    item_count: number;
    status: string;
  };
  error?: string;
}

/**
 * Generate listicle items for a given topic
 */
export async function generateListicleItems(
  request: GenerateListicleItemsRequest,
): Promise<GenerateListicleItemsResponse> {
  const response = await fetch(`${pb.baseURL}/api/listicle/items`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token || "",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.text();
    console.error("[LISTICLE SERVICE] Failed to generate items:", error);
    throw new Error(`Failed to generate listicle items: ${error}`);
  }

  return await response.json();
}

/**
 * Generate a complete listicle article from items
 */
export async function generateListicleArticle(
  request: GenerateListicleArticleRequest,
): Promise<GenerateListicleArticleResponse> {
  const response = await fetch(`${pb.baseURL}/api/listicle/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token || "",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.text();
    console.error("[LISTICLE SERVICE] Failed to generate article:", error);
    throw new Error(`Failed to generate listicle article: ${error}`);
  }

  return await response.json();
}

/**
 * Generate an article with real-time progress tracking
 * This creates a post record immediately and returns its ID for real-time subscription
 */
export async function generateArticle(
  request: GenerateArticleRequest,
): Promise<GenerateArticleResponse> {
  const response = await fetch(`${pb.baseURL}/api/v1/posts/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token || "",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.text();
    console.error("[ARTICLE SERVICE] Failed to generate article:", error);
    throw new Error(`Failed to generate article: ${error}`);
  }

  return await response.json();
}

/**
 * Get all posts that are currently generating for a brand
 */
export async function getGeneratingPosts(brandId: string) {
  try {
    const posts = await pb.collection("posts").getList(1, 50, {
      filter: `brand = "${brandId}" && status = "generating"`,
      sort: "-created",
    });
    return posts.items;
  } catch (error) {
    console.error("Error fetching generating posts:", error);
    return [];
  }
}

/**
 * Cancel article generation (update status to failed)
 */
export async function cancelArticleGeneration(postId: string) {
  try {
    await pb.collection("posts").update(postId, {
      status: "failed",
      generationError: "Generation cancelled by user",
      progress: JSON.stringify({
        stage: "cancelled",
        percentage: 0,
        message: "Generation cancelled by user",
        lastUpdated: new Date().toISOString(),
        isCompleted: false,
        hasError: true,
      }),
    });
    return true;
  } catch (error) {
    console.error("Error cancelling article generation:", error);
    return false;
  }
}
